/**
 * Constant-value wiring for MappingPhase (column-mapping-ui spec, "Constant and
 * value-map editors"): a fixed value for a target field with no source column
 * (primarily Dostawca/supplier). Fields required by the system but entirely
 * absent from the file are surfaced here as resolvable-via-constant chips,
 * replacing the plain read-only error the field used to render as.
 */
import { Pencil } from 'lucide-react';
import { MediaTypeEnum } from '@ooh/contracts';
import { Button } from '@ooh/ui/components/button';
import { Badge } from '@ooh/ui/components/badge';
import { formatTemplate } from '@ooh/ui/lib/utils';
import { MEDIA_TYPE_FIELD_KEY } from '../lib/collect-mapped-field-keys';
import {
  ConstantEditorDialog,
  type ConstantEditorAllowedValue,
  type ConstantEditorLabels,
} from './mapping-transforms-constant-dialog';
import {
  getEnumValuesForFieldKey,
  isQueryBackedEnumFieldKey,
} from '../lib/constant-value-enum-options';
import { FormatEnumOptions } from './format-enum-options';
import type { ColumnMappingItem, FieldOptionItem, MappingPhaseLabels } from './mapping-phase';

export function buildConstantEditorLabels(labels: MappingPhaseLabels): ConstantEditorLabels {
  return {
    dialogTitle: labels.constantDialogTitle,
    columnLabelPrefix: labels.patternColumnLabelPrefix,
    valueFieldLabel: labels.constantValueFieldLabel,
    doneButtonLabel: labels.patternDoneButtonLabel,
    valueSearchPlaceholder: labels.constantValueSearchPlaceholder,
    valueNoResultsLabel: labels.fieldNoResults,
    enumOptionsLoadingLabel: labels.enumOptionsLoadingLabel,
    enumOptionsErrorLabel: labels.enumOptionsErrorLabel,
  };
}

/** Builds the Select options for the `type` field's constant editor (Bug 1/3: "Typ nośnika"
 * has a closed set of values — MediaTypeEnum — so its constant editor must offer a Select
 * instead of a free-text Input). Labels come from MappingPhaseLabels.constantTypeOptionLabels. */
export function buildConstantTypeAllowedValues(
  labels: MappingPhaseLabels,
): ConstantEditorAllowedValue[] {
  return MediaTypeEnum.options.map((mt) => ({
    value: mt,
    label: labels.constantTypeOptionLabels[mt] ?? mt,
  }));
}

export function buildMissingRequiredFieldsLabels(
  labels: MappingPhaseLabels,
): MissingRequiredFieldsLabels {
  return {
    missingRequiredHeading: labels.missingRequiredHeading,
    missingRequiredHint: labels.missingRequiredHint,
    constantEditAriaLabel: labels.constantEditAriaLabel,
    constantAssignedTemplate: labels.constantAssignedTemplate,
  };
}

/** Sentinel id prefix for synthetic constant-assignment mapping entries (no real source column). */
const CONSTANT_MAPPING_ID_PREFIX = 'constant-';

/**
 * True when a constant assignment for a field is a non-empty, non-blank value — an
 * empty/whitespace-only constant does NOT satisfy a required field (import-mapping-transforms
 * spec: "Empty constant does not satisfy a required field"), consistent with the api's
 * required-field validation (validate-row.ts writes the constant verbatim, then the existing
 * field_required check flags a blank value).
 */
export function hasResolvedConstantValue(value: string | undefined): value is string {
  return value != null && value.trim() !== '';
}

export function applyConstantAssignment(
  assignments: Record<string, string>,
  fieldKey: string,
  value: string,
): Record<string, string> {
  return { ...assignments, [fieldKey]: value };
}

/** Builds synthetic ColumnMappingItem entries (transform: constant) for confirm-mapping, one per assigned field. */
export function buildConstantMappingItems(
  assignments: Record<string, string>,
): ColumnMappingItem[] {
  return Object.entries(assignments).map(([fieldKey, value]) => ({
    id: `${CONSTANT_MAPPING_ID_PREFIX}${fieldKey}`,
    sourceColumn: '',
    mappedTo: fieldKey,
    transform: { type: 'constant', value },
  }));
}

export interface MissingRequiredFieldsLabels {
  missingRequiredHeading: string;
  missingRequiredHint: string;
  constantEditAriaLabel: string;
  /** Template with a `{value}` token, e.g. "→ stała: {value}" */
  constantAssignedTemplate: string;
}

export interface MissingRequiredFieldsSectionProps {
  missingRequiredFields: FieldOptionItem[];
  constantAssignments: Record<string, string>;
  labels: MissingRequiredFieldsLabels;
  onEditConstant: (fieldKey: string) => void;
}

export function MissingRequiredFieldsSection({
  missingRequiredFields,
  constantAssignments,
  labels,
  onEditConstant,
}: MissingRequiredFieldsSectionProps) {
  if (missingRequiredFields.length === 0) return null;
  return (
    <div
      className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 space-y-2"
      data-testid="missing-required-section"
    >
      <div className="space-y-1">
        <p className="text-sm font-semibold text-destructive">{labels.missingRequiredHeading}</p>
        <p className="text-xs text-destructive">{labels.missingRequiredHint}</p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {missingRequiredFields.map((field) => {
          const assignedValue = constantAssignments[field.key];
          const isAssigned = hasResolvedConstantValue(assignedValue);
          return (
            <Badge
              key={field.key}
              variant={isAssigned ? 'success' : 'destructive'}
              className="gap-1"
              data-testid={`missing-required-chip-${field.key}`}
            >
              {isAssigned
                ? `${field.label} ${formatTemplate(labels.constantAssignedTemplate, { value: assignedValue })}`
                : field.label}
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="h-4 w-4 p-0 shrink-0"
                aria-label={labels.constantEditAriaLabel}
                data-testid={`edit-constant-${field.key}`}
                onClick={() => onEditConstant(field.key)}
              >
                <Pencil className="h-3 w-3" />
              </Button>
            </Badge>
          );
        })}
      </div>
    </div>
  );
}

export interface MappingConstantEditorSlotProps {
  editingFieldKey: string | null;
  fieldOptions: FieldOptionItem[];
  constantAssignments: Record<string, string>;
  labels: ConstantEditorLabels;
  /** MediaTypeEnum Select options offered when editingFieldKey is the `type` field. */
  mediaTypeAllowedValues: ConstantEditorAllowedValue[];
  onChange: (fieldKey: string, value: string) => void;
  onClose: () => void;
}

interface QueryBackedConstantEditorProps {
  sourceColumnLabel: string;
  initialValue: string;
  labels: ConstantEditorLabels;
  onChange: (value: string) => void;
  onClose: () => void;
}

/**
 * Renders ConstantEditorDialog for a query-backed enum field (currently only 'format') —
 * isolated into its own component so the format dictionary is only requested while this
 * specific field is being edited. Every other field's constant editor stays on the
 * synchronous static-enum path in MappingConstantEditorSlot below.
 */
function QueryBackedConstantEditor({
  sourceColumnLabel,
  initialValue,
  labels,
  onChange,
  onClose,
}: QueryBackedConstantEditorProps) {
  return (
    <FormatEnumOptions>
      {({ options, isLoading, isError }) => (
        <ConstantEditorDialog
          sourceColumnLabel={sourceColumnLabel}
          initialValue={initialValue}
          labels={labels}
          enumOptions={options}
          enumOptionsLoading={isLoading}
          enumOptionsError={isError}
          onChange={onChange}
          onClose={onClose}
        />
      )}
    </FormatEnumOptions>
  );
}

/** Renders ConstantEditorDialog for the missing-required field currently being edited, or nothing. */
export function MappingConstantEditorSlot({
  editingFieldKey,
  fieldOptions,
  constantAssignments,
  labels,
  mediaTypeAllowedValues,
  onChange,
  onClose,
}: MappingConstantEditorSlotProps) {
  if (editingFieldKey == null) return null;
  const field = fieldOptions.find((o) => o.key === editingFieldKey);
  if (!field) return null;

  if (isQueryBackedEnumFieldKey(editingFieldKey)) {
    return (
      <QueryBackedConstantEditor
        sourceColumnLabel={field.label}
        initialValue={constantAssignments[editingFieldKey] ?? ''}
        labels={labels}
        onChange={(value) => onChange(editingFieldKey, value)}
        onClose={onClose}
      />
    );
  }

  return (
    <ConstantEditorDialog
      sourceColumnLabel={field.label}
      initialValue={constantAssignments[editingFieldKey] ?? ''}
      labels={labels}
      allowedValues={editingFieldKey === MEDIA_TYPE_FIELD_KEY ? mediaTypeAllowedValues : undefined}
      enumOptions={getEnumValuesForFieldKey(editingFieldKey)}
      onChange={(value) => onChange(editingFieldKey, value)}
      onClose={onClose}
    />
  );
}
